import type { AuthorStats } from './types';

export type KritikerId = 'klassekampen' | 'aftenposten' | 'morgenbladet' | 'nrk-bok';

export type TerningTall = 1 | 2 | 3 | 4 | 5 | 6;

export type Kritiker = {
  id: KritikerId;
  navn: string;
  publikasjon: string;
  sjangerVekt: Record<string, number>;
  statVekt: Partial<Record<keyof AuthorStats, number>>;
  templates: Record<string, Record<TerningTall, string>>;
};

const DEFAULT_SJANGER_VEKT = 0.5;

const SHARED_GENRES = [
  'KRIM',
  'ROMANCE',
  'LITERARY',
  'BARNEBOK',
  'POETRY',
  'NONFICTION',
  'FANTASY',
];

export const KRITIKERE: Record<KritikerId, Kritiker> = {
  klassekampen: {
    id: 'klassekampen',
    navn: 'Klassekampen',
    publikasjon: 'Klassekampen',
    sjangerVekt: {
      LITERARY: 1.0,
      POETRY: 1.0,
      NONFICTION: 0.9,
      KRIM: 0.6,
      FANTASY: 0.5,
      BARNEBOK: 0.55,
      ROMANCE: 0.35,
    },
    statVekt: {
      voice: 0.25,
      autentisitet: 0.2,
      spraak: 0.2,
      karakter: 0.15,
      plot: 0.1,
      dialog: 0.05,
      disiplin: 0.05,
    },
    templates: {
      KRIM: {
        6: '{forfatter} skriver krim som om språket selv er åstedet. {tittel} er et lite mesterverk i en sjanger som sjelden tør å være litteratur.',
        5: 'Endelig en krim som tør å spørre hva sjangeren er for noe. {tittel} bærer både plottet og en kritikk av det.',
        4: 'Solid håndverk, men {tittel} mangler en ideologisk ryggrad som kunne løftet boka.',
        3: 'Velmenende, men trygt. {forfatter} skriver det vi har lest før, bedre kledd.',
        2: 'Bekymringsverdig konvensjonell. {tittel} bekrefter mer enn den utfordrer.',
        1: 'En patetisk øvelse i å passe inn. {forfatter} skriver for et marked, ikke for litteraturen.',
      },
      ROMANCE: {
        6: '{forfatter} løfter romansen fra sjanger til samtid. {tittel} sier mer om begjær og klasse enn de fleste i år.',
        5: 'Romanseformen brukes som verktøy, ikke som sluttmål. {tittel} har en uventet politisk presisjon.',
        4: '{tittel} har øyeblikk av virkelig observasjon, men formen forblir tam.',
        3: 'Konvensjonell romanse, kompetent skrevet, uten ambisjon utover sjangeren.',
        2: '{forfatter} bekrefter at romansen fortsatt er kapitalismens favorittfortelling.',
        1: 'Lesetid stjålet. {tittel} er en katalog over forhåndsgodkjente følelser.',
      },
      LITERARY: {
        6: 'Dette er litteratur som forandrer hva man kan tenke etter at boka er lagt fra seg. {forfatter} skriver i en klasse for seg.',
        5: 'Endelig en debutroman som tør å pirke i borgerligheten. {tittel} bærer en sjelden språklig presisjon.',
        4: 'Ambisjonen er uomtvistelig. {forfatter} treffer ikke alltid, men prøver der det betyr noe.',
        3: 'Velskrevet, men trygt. {tittel} forblir innenfor det vi allerede vet om norsk samtid.',
        2: '{forfatter} skriver pent om det vi allerede er enige om. Ikke nok.',
        1: 'Borgerlig velmenende, intellektuelt slapt. Vi har lest dette manuset flere ganger, av andre forfattere.',
      },
      BARNEBOK: {
        6: '{forfatter} viser respekt for barneleseren som en politisk og intellektuell aktør. Sjeldent.',
        5: '{tittel} stoler på at barn tåler mer enn vi tror. Et viktig bidrag.',
        4: 'God tone, men bildebok-sjangerens muligheter for samfunnskritikk forblir uutnyttet.',
        3: 'Hyggelig, men forutsigbar. Barn fortjener tekster med mer kant.',
        2: '{tittel} bekrefter at barnelitteratur fortsatt er et beroligende marked, ikke en kunstform.',
        1: 'Pedagogisk pekefinger uten politisk innhold. Synd på leseren, uansett alder.',
      },
      POETRY: {
        6: '{forfatter} skriver dikt som ingen andre i Norden akkurat nå. {tittel} kommer til å bli lest om tjue år.',
        5: 'Et språk som tør å være vanskelig fordi det vil noe. {tittel} fortjener sin plass i kanon.',
        4: 'Lovende, men {forfatter} stoler ennå på poetiske krykker som burde vært kastet.',
        3: 'Solide enkeltlinjer, men samlingen som helhet mangler nødvendighet.',
        2: 'Mye følt, lite tenkt. {tittel} er stemningslyrikk i et tiår som krever mer.',
        1: 'Diktene i {tittel} er private til skade for leseren. Forfatteren har enda en samling i seg, forhåpentligvis bedre.',
      },
      NONFICTION: {
        6: '{forfatter} har skrevet årets viktigste sakprosa. {tittel} kommer til å forme debatten i lang tid.',
        5: 'En undersøkelse som ikke er redd for konsekvensene av sine egne funn.',
        4: 'Saksforholdet er solid. Den teoretiske ambisjonen vakler.',
        3: 'Greit dokumentert, men {forfatter} mangler analytisk dristighet.',
        2: '{tittel} bekrefter eksisterende narrativer uten å utfordre dem.',
        1: 'Sakprosa i navnet, propaganda i strukturen.',
      },
      FANTASY: {
        6: '{forfatter} bruker sjangeren som politisk speil. {tittel} er fantasy som tenker.',
        5: 'Verdensbyggingen tjener en idé, ikke omvendt. Lovende.',
        4: '{tittel} har øyeblikk av virkelig tenkning, men eskaperer for ofte inn i sjangerens komfort.',
        3: 'Kompetent fantasy som ikke vil noe utover sjangeren.',
        2: 'Eskapisme uten selvinnsikt. {forfatter} kunne tatt sjansen — gjorde det ikke.',
        1: 'Tolkien-resirkulering i 2026. Vi forventer mer av norsk fantasy.',
      },
    },
  },

  aftenposten: {
    id: 'aftenposten',
    navn: 'Aftenposten',
    publikasjon: 'Aftenposten (Helgebilag)',
    sjangerVekt: {
      LITERARY: 1.0,
      KRIM: 0.9,
      NONFICTION: 0.85,
      BARNEBOK: 0.7,
      ROMANCE: 0.65,
      POETRY: 0.6,
      FANTASY: 0.5,
    },
    statVekt: {
      spraak: 0.2,
      plot: 0.2,
      karakter: 0.2,
      dialog: 0.15,
      autentisitet: 0.1,
      voice: 0.1,
      disiplin: 0.05,
    },
    templates: {
      KRIM: {
        6: '{forfatter} har skrevet en krim som er like mye roman. {tittel} hever sjangeren uten å forlate den.',
        5: 'Solid plottet, vakkert språk. {tittel} er krim slik man ønsker krim var oftere.',
        4: '{forfatter} kan håndverket. Et par overflødige avsnitt, men en gjennomarbeidet bok.',
        3: 'Greit fortalt, men plottet bærer ikke alltid. En forfatter med mer i seg.',
        2: '{tittel} velger korteste vei til oppklaring. Det merkes.',
        1: 'Forutsigbart fra første kapittel. Krim trenger ikke å være så lettvint.',
      },
      ROMANCE: {
        6: '{forfatter} skriver om kjærlighet med en presisjon som overrasker. {tittel} blir lest av flere generasjoner.',
        5: 'Varm og ærlig. {tittel} klarer det vanskelige — å være rørende uten å bli sentimental.',
        4: 'Gode dialoger, men handlingsbuen er kjent. {forfatter} kan mer enn dette.',
        3: 'Trygg sommerlesning. Den finner sitt publikum.',
        2: '{tittel} er kompetent, men hjertet ligger på feil sted i fortellingen.',
        1: 'Klisjéer på rekke og rad. Sjangeren fortjener bedre.',
      },
      LITERARY: {
        6: 'En bok som blir værende i kroppen lenge etter siste side. {forfatter} skriver i internasjonal klasse.',
        5: 'Solid håndverk og en historie som blir sittende. {tittel} er romankunst i klassisk forstand.',
        4: '{forfatter} kan språket og kjenner formen. {tittel} hadde tjent på en strammere redaktør.',
        3: 'Greit fortalt, men språket bærer ikke alltid. Lovende, ikke ferdig.',
        2: '{tittel} vil noe, men finner ikke veien dit. Forfatteren har mye å lære.',
        1: 'Forfatteren har mye å lære før neste forsøk. {tittel} burde aldri kommet ut.',
      },
      BARNEBOK: {
        6: '{forfatter} skriver for barn med samme alvor som for voksne. {tittel} blir en klassiker.',
        5: 'Varm, klok og morsom. {tittel} fortjener sin plass på nattbordet.',
        4: 'Sjarmerende, men slutten henger ikke helt sammen. Barna vil likevel like den.',
        3: 'Greit lesestoff, ikke noe man husker etter en uke.',
        2: '{tittel} er pedagogisk uten å være kunstnerisk.',
        1: 'Forfatteren har ikke barneboklesere som målgruppe. Det merkes.',
      },
      POETRY: {
        6: '{forfatter} skriver dikt som åpner språket på nytt. {tittel} er et lite mirakel.',
        5: 'Klar stemme, solid håndverk. {tittel} fortjener flere lesere enn lyrikken vanligvis får.',
        4: 'Lovende dikt blandet med svakere. En samling som kunne vært strammere.',
        3: '{forfatter} har øyeblikk, men ikke helhet. En vei å gå.',
        2: 'Privat lyrikk som ikke åpner seg utover.',
        1: '{tittel} burde forblitt skrivebordsskuff. Beklager — vi er ærlige fordi vi bryr oss.',
      },
      NONFICTION: {
        6: '{forfatter} har skrevet bokhøstens viktigste sakprosa. {tittel} setter en standard.',
        5: 'Grundig research, god prosa, viktig tema. Et solid bidrag til debatten.',
        4: 'Et sentralt tema fortjener en mer dristig analyse. Likevel verdt å lese.',
        3: 'Greit dokumentert, ikke spesielt overraskende.',
        2: '{tittel} oppsummerer mer enn den undersøker.',
        1: 'Sakprosa krever mer enn velmente meninger. Her stopper det ved meningene.',
      },
      FANTASY: {
        6: '{forfatter} skriver fantasy som overrasker en gammel skeptiker. {tittel} fortjener internasjonalt publikum.',
        5: 'Gjennomarbeidet verden, troverdige karakterer. Sjangerens beste på lenge.',
        4: '{tittel} har gode partier, men noen kapitler er for lange.',
        3: 'Trygg fantasy, kompetent levert. Fans får det de vil ha.',
        2: '{tittel} resirkulerer kjente troper uten å fornye dem.',
        1: 'Forfatteren har lest mye fantasy. Det er ikke det samme som å skrive den.',
      },
    },
  },

  morgenbladet: {
    id: 'morgenbladet',
    navn: 'Morgenbladet',
    publikasjon: 'Morgenbladet',
    sjangerVekt: {
      POETRY: 1.0,
      NONFICTION: 1.0,
      LITERARY: 0.95,
      KRIM: 0.6,
      BARNEBOK: 0.5,
      FANTASY: 0.45,
      ROMANCE: 0.3,
    },
    statVekt: {
      voice: 0.25,
      spraak: 0.25,
      autentisitet: 0.2,
      karakter: 0.15,
      plot: 0.05,
      dialog: 0.05,
      disiplin: 0.05,
    },
    templates: {
      KRIM: {
        6: '{forfatter} skriver krim som filosofisk undersøkelse. {tittel} stiller spørsmål sjangeren sjelden får stille.',
        5: 'Mørket i {tittel} har tyngde. Krim som faktisk bærer det den lover.',
        4: 'Lovende, men {forfatter} viker unna det vanskeligste når det blir alvor.',
        3: 'Sjangerøvelse uten metafysisk nerve.',
        2: '{tittel} forveksler vold med dybde. Det er ikke det samme.',
        1: 'Krim i sin minst interessante form. Vi forventer mer av norske forfattere.',
      },
      ROMANCE: {
        6: '{forfatter} skriver om eros med både filosofisk og kroppslig presisjon. Sjeldent.',
        5: 'Begjær brukt som tenkende kraft, ikke som plot-motor. Imponerende.',
        4: '{tittel} har øyeblikk av ekte innsikt, men formen er for ettergivende.',
        3: 'Konvensjonell romanse. Ikke vår sjanger, men kompetent.',
        2: 'Lite å hente filosofisk i {tittel}. Kjærligheten forblir overflate.',
        1: 'Sjangeren har gode forfattere — denne er ikke blant dem.',
      },
      LITERARY: {
        6: 'Året kommer ikke til å gi oss mange bøker som {tittel}. Et verk, ikke en bok.',
        5: '{forfatter} skriver en prosa som tenker. Sjeldent og nødvendig.',
        4: 'Ambisjonen er reell. Gjennomføringen henter ikke alltid inn ambisjonen.',
        3: 'Et solid debutarbeid som lover mer enn det innfrir.',
        2: 'Pen prosa, lite på spill. {tittel} risikerer ingenting.',
        1: 'Bekymringsverdig overflatisk gitt temaets alvor.',
      },
      BARNEBOK: {
        6: '{forfatter} respekterer barnet som tenkende vesen. {tittel} er en sjelden glede å lese for voksne også.',
        5: 'Klok, vakker, urolig på riktig måte. Barnelitteratur som bør anmeldes som litteratur.',
        4: '{tittel} har språklig kvalitet, men temaet håndteres for forsiktig.',
        3: 'Pen barnebok uten å være mer enn det.',
        2: '{forfatter} undervurderer leseren sin. Barn tåler mer.',
        1: '{tittel} er pedagogisk i feil retning. Litteratur skal ikke berolige.',
      },
      POETRY: {
        6: '{forfatter} skriver dikt som vil leses i førti år. {tittel} er årets viktigste lyrikk-samling.',
        5: 'Et lite mesterverk for de som vil grave dypere. Sjelden formell presisjon.',
        4: 'Ambisjonen er der, gjennomføringen vakler i de svakeste partiene.',
        3: '{forfatter} har en stemme, ikke ennå et samlet uttrykk.',
        2: 'Stemningslyrikk som mangler nødvendighet.',
        1: '{tittel} bekrefter at samlingen burde vært ledet av en strengere redaktør.',
      },
      NONFICTION: {
        6: '{forfatter} skriver sakprosa på europeisk nivå. {tittel} kommer til å bli sitert i lang tid.',
        5: 'Grundig, modig, formelt overbevisende. Et viktig bidrag.',
        4: 'Solid tema, men den teoretiske rammen er svakere enn materialet fortjener.',
        3: 'Saksforholdet er greit, analysen blir overflate.',
        2: 'Bekymringsverdig overflatisk gitt temaets alvor.',
        1: 'Velmenende meninger uttalt med overbevisning. Ikke det samme som tenkning.',
      },
      FANTASY: {
        6: '{forfatter} bruker sjangeren for å si noe sjangeren sjelden kan si. {tittel} fortjener oppmerksomhet utover fan-miljøet.',
        5: 'Tenkende fantasy. Et lite gjennombrudd for sjangeren i Norge.',
        4: '{tittel} har øyeblikk av virkelig tenkning, men eskaperer der det burde stoppet opp.',
        3: 'Sjangerkompetent, ikke filosofisk ambisiøst.',
        2: 'Vi er ikke imot fantasy som form. Vi er imot fantasy som unngåelse.',
        1: '{tittel} bekrefter sjangerens svakeste tendenser. Synd.',
      },
    },
  },

  'nrk-bok': {
    id: 'nrk-bok',
    navn: 'NRK Bok',
    publikasjon: 'NRK Bok (nett, Bok i P2)',
    sjangerVekt: {
      KRIM: 1.0,
      BARNEBOK: 0.95,
      ROMANCE: 0.9,
      NONFICTION: 0.85,
      LITERARY: 0.8,
      FANTASY: 0.7,
      POETRY: 0.55,
    },
    statVekt: {
      plot: 0.2,
      dialog: 0.2,
      karakter: 0.2,
      autentisitet: 0.15,
      spraak: 0.1,
      voice: 0.1,
      disiplin: 0.05,
    },
    templates: {
      KRIM: {
        6: 'Du klarer ikke legge {tittel} fra deg. {forfatter} har skrevet en krim som vil bli sammenlignet med de største.',
        5: 'Spennende fra første side til siste. En klar bokklubb-anbefaling.',
        4: '{tittel} har et godt plot, men slutten kunne vært strammere.',
        3: 'Solid sommerkrim. Den finner sitt publikum.',
        2: '{forfatter} skriver greit, men plottet henger ikke helt sammen.',
        1: 'Vanskelig å holde interessen. Bedre krim finnes.',
      },
      ROMANCE: {
        6: '{tittel} er en av de bøkene du gir til venninna på bursdagen. Varm, klok, vakkert skrevet.',
        5: 'En bok du gjerne anbefaler bokklubben. Følelsene treffer.',
        4: 'Hyggelig lesning. {forfatter} har en god stemme, kan bli enda bedre.',
        3: 'Trygg sommerlesning. Finner sitt publikum på stranda.',
        2: '{tittel} er litt forutsigbar. Likevel kos for noen.',
        1: 'Klisjé etter klisjé. Selv romansefans har lest dette før.',
      },
      LITERARY: {
        6: '{forfatter} har skrevet høstens viktigste roman. {tittel} bør stå på alle nattbord.',
        5: 'En bok du gjerne anbefaler bokklubben — og som faktisk tåler diskusjon.',
        4: 'Vakkert språk, varm fortelling. Et par lange partier i midten.',
        3: 'Greit fortalt. Finner kanskje veien til bokklubben likevel.',
        2: '{tittel} sliter med å holde leseren med seg.',
        1: 'Vanskelig å holde interessen. Forfatteren bør finne sin form.',
      },
      BARNEBOK: {
        6: '{forfatter} har skrevet boka som blir lest hver kveld i seks måneder. Helt nydelig.',
        5: 'Varm, morsom, vakker. En klar anbefaling til barnebokklubben.',
        4: 'God historie, fine illustrasjoner. Litt langtekkelig i midten.',
        3: 'Hyggelig barnebok, ikke en klassiker. Holder i flere lesinger.',
        2: '{tittel} mangler nerve. Barna går videre raskt.',
        1: 'Vanskelig å holde barna interessert. Skuffende.',
      },
      POETRY: {
        6: 'Selv den som ikke pleier å lese lyrikk vil elske {tittel}. {forfatter} skriver tilgjengelig og vakkert.',
        5: 'Klar stemme, dikt man husker. En fin inngang for nye lyrikklesere.',
        4: 'Enkelte dikt sitter igjen, andre forsvinner. En lovende samling.',
        3: 'Lyrikk er ikke for alle. Denne treffer noen, men ikke mange.',
        2: '{tittel} er privat på en måte som stenger leseren ute.',
        1: 'Vanskelig tilgjengelig uten å virke verdt strevet.',
      },
      NONFICTION: {
        6: '{forfatter} formidler komplisert stoff slik at alle henger med. {tittel} blir høstens samtalebok.',
        5: 'Lærerikt og engasjerende skrevet. Et tema vi alle bør kunne mer om.',
        4: 'Viktig tema, kompetent skrevet. Et par tørre kapitler.',
        3: 'Greit faktastoff. {forfatter} kunne dratt leseren tettere inn.',
        2: '{tittel} blir for tørr for den brede leseren.',
        1: 'Vanskelig å holde interessen. Stoffet kunne fortjent en annen formidler.',
      },
      FANTASY: {
        6: '{forfatter} har skrevet norsk fantasys store øyeblikk. {tittel} bør oversettes umiddelbart.',
        5: 'Spennende verden, sterke karakterer. En klar anbefaling til fantasylesere — og andre.',
        4: 'Godt univers, men handlingen henger på den lange siden.',
        3: 'Solid sjangerlesning. Fans vil like den.',
        2: '{tittel} sliter med å skille seg fra mengden.',
        1: 'Vanskelig å holde interessen oppe gjennom 500 sider.',
      },
    },
  },
};

export const KRITIKER_IDS: KritikerId[] = ['klassekampen', 'aftenposten', 'morgenbladet', 'nrk-bok'];

export function alleKritikere(): Kritiker[] {
  return KRITIKER_IDS.map((id) => KRITIKERE[id]);
}

export function sjangerVektFor(kritiker: Kritiker, sjanger: string): number {
  return kritiker.sjangerVekt[sjanger] ?? DEFAULT_SJANGER_VEKT;
}

export function templateFor(
  kritiker: Kritiker,
  sjanger: string,
  terninger: TerningTall,
): string {
  const sjangerLookup = kritiker.templates[sjanger] ?? kritiker.templates.LITERARY;
  const fallback = kritiker.templates.LITERARY?.[terninger] ?? '';
  return sjangerLookup?.[terninger] ?? fallback;
}

export function alleSjangre(): readonly string[] {
  return SHARED_GENRES;
}
